import React, { useEffect, useState } from "react";
import Header from "@components/Common/Header/Header";
import Footer from "@components/Common/Footer/Footer";
import {
  getPrivacyPolicy,
  PrivacyPolicy,
  PrivacyPolicyResponse,
} from "../../API/PrivacyPolicy";
import { formatPolicyContent } from "@utils/formatPolicyContent";
import "./Privacy.css";

const formatLastUpdated = (value?: string | null) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const PrivacyPolicyHeader = ({
  title,
  lastUpdated,
}: {
  title: string;
  lastUpdated: string | null;
}) => (
  <>
    <h1 className="privacy-policy-title">{title}</h1>
    {lastUpdated && (
      <p className="privacy-policy-meta">Last Updated: {lastUpdated}</p>
    )}
  </>
);

const PrivacyPolicyFallback = () => (
  <>
    <h2>1. Introduction</h2>
    <h3>1.1 Purpose of this privacy policy</h3>
    <p>
      This privacy policy aims to give you information on how Lendriva collects
      and processes your personal data through your use of this platform,
      including any data you may provide when you sign up, apply for a loan, or
      lend funds.
    </p>
    <p>
      <strong>Personal data</strong> means any information relating to an
      identified or identifiable individual. By using our services, you agree to
      the practices described in this policy.
    </p>

    <h3>1.2 Controller</h3>
    <p>
      Lendriva is the controller and responsible for your personal data. If you
      have any questions about this privacy policy, please contact us using the
      details set out in section 9 below.
    </p>

    <h2>2. The Data We Collect About You</h2>
    <p>
      We may collect, use, store and transfer different kinds of personal data
      about you, which we have grouped together as follows:
    </p>
    <ul>
      <li>
        <strong>Identity Data</strong> includes first name, last name, username
        or similar identifier, date of birth, and identification documents
        required for KYC verification.
      </li>
      <li>
        <strong>Contact Data</strong> includes email address, telephone numbers,
        and correspondence address.
      </li>
      <li>
        <strong>Financial Data</strong> includes bank account details, wallet
        transactions, loan amounts, interest rates, and credit scores used to
        process loans and manage funds.
      </li>
      <li>
        <strong>Technical Data</strong> includes internet protocol (IP) address,
        browser type and version, device information, and browsing activity.
      </li>
      <li>
        <strong>Usage Data</strong> includes information about how you use our
        website, applications, and services.
      </li>
    </ul>

    <h3>2.1 How is your personal data collected?</h3>
    <p>We use different methods to collect data from and about you, including:</p>
    <ul>
      <li>
        <strong>Direct interactions.</strong> You may give us your Identity,
        Contact and Financial Data by filling in forms or by corresponding with
        us by post, phone, email or otherwise.
      </li>
      <li>
        <strong>Automated technologies or interactions.</strong> As you interact
        with our platform, we may automatically collect Technical Data about
        your equipment, browsing actions and patterns.
      </li>
      <li>
        <strong>Third parties or publicly available sources.</strong> We may
        receive personal data about you from credit reference agencies, identity
        verification providers, and payment service providers.
      </li>
    </ul>

    <h2>3. How We Use Your Personal Data</h2>
    <p>
      We will only use your personal data when the law allows us to. Most
      commonly, we will use your personal data in the following circumstances:
    </p>
    <ul>
      <li>
        Where we need to perform the contract we are about to enter into or have
        entered into with you.
      </li>
      <li>
        Where it is necessary for our legitimate interests (or those of a third
        party) and your interests and fundamental rights do not override those
        interests.
      </li>
      <li>Where we need to comply with a legal or regulatory obligation.</li>
    </ul>

    <h3>3.1 Purposes for which we will use your personal data</h3>
    <p>We have set out below a description of the ways we plan to use your personal data:</p>
    <ul>
      <li>To register you as a new customer and verify your identity.</li>
      <li>To process and deliver your loan application or lending activity.</li>
      <li>To manage our relationship with you, including notifying you about changes to our terms or privacy policy.</li>
      <li>To administer and protect our business and this platform (including troubleshooting, data analysis, testing, and system maintenance).</li>
      <li>To deliver relevant content and advertisements to you and measure or understand the effectiveness of the advertising we serve to you.</li>
    </ul>

    <h2>4. Data Security</h2>
    <p>
      We have put in place appropriate security measures to prevent your personal
      data from being accidentally lost, used or accessed in an unauthorised way,
      altered or disclosed. In addition, we limit access to your personal data to
      those employees, agents, contractors and other third parties who have a
      business need to know.
    </p>

    <h2>5. Data Retention</h2>
    <p>
      We will only retain your personal data for as long as reasonably necessary
      to fulfil the purposes we collected it for, including for the purposes of
      satisfying any legal, regulatory, tax, accounting or reporting
      requirements. We may retain transaction records for up to 10 years
      following the end of our relationship with you.
    </p>

    <h2>6. Your Legal Rights</h2>
    <p>
      Under certain circumstances, you have rights under data protection laws in
      relation to your personal data, including the right to:
    </p>
    <ul>
      <li>Request access to your personal data.</li>
      <li>Request correction of your personal data.</li>
      <li>Request erasure of your personal data.</li>
      <li>Object to processing of your personal data.</li>
      <li>Request restriction of processing your personal data.</li>
      <li>Request transfer of your personal data.</li>
      <li>Withdraw consent at any time where we are relying on consent to process your personal data.</li>
    </ul>
    <p>
      If you wish to exercise any of the rights set out above, please contact us
      at <a href="mailto:support@lendriva.com">support@lendriva.com</a>.
    </p>

    <h2>7. Changes to This Privacy Policy</h2>
    <p>
      We may update this privacy policy from time to time. Any changes will be
      posted on this page with an updated revision date. We encourage you to
      review this policy periodically.
    </p>

    <h2>8. Contact Us</h2>
    <p>
      If you have any questions about this privacy policy or our privacy
      practices, please contact us:
    </p>
    <ul>
      <li>
        <strong>Email address:</strong>{" "}
        <a href="mailto:support@lendriva.com">support@lendriva.com</a>
      </li>
      <li>
        <strong>Postal address:</strong> [Insert Address]
      </li>
    </ul>
  </>
);

const Privacy = () => {
  const handleMenuToggle = () => {};
  const [policy, setPolicy] = useState<PrivacyPolicy | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    getPrivacyPolicy()
      .then((res: PrivacyPolicyResponse) => setPolicy(res.data))
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, []);

  const title = policy?.title || "Privacy Policy";
  const lastUpdated = formatLastUpdated(policy?.updatedAt);

  return (
    <div className="privacy-policy-page">
      <Header handleMenuToggle={handleMenuToggle} />

      <section className="privacy-policy-content">
        <div className="container">
          {isLoading && (
            <p className="privacy-policy-loading">Loading privacy policy...</p>
          )}

          {!isLoading && policy?.content && (
            <div className="privacy-policy-body">
              <PrivacyPolicyHeader title={title} lastUpdated={lastUpdated} />
              <div
                className="privacy-policy-html"
                dangerouslySetInnerHTML={{
                  __html: formatPolicyContent(policy.content),
                }}
              />
            </div>
          )}

          {!isLoading && !policy?.content && (
            <div className="privacy-policy-body">
              <PrivacyPolicyHeader title={title} lastUpdated={lastUpdated} />
              {isError && (
                <p className="privacy-policy-error">
                  Unable to load the latest privacy policy. Showing default
                  content below.
                </p>
              )}
              <PrivacyPolicyFallback />
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Privacy;
